// FNP-1557: read the repo-name suffix the ORCHESTRATOR will actually use.
//
// ═══ WHY THIS IS READ AND NOT COMPUTED ═══
//
// Every repository and pipeline name a provision creates is
// `${baseName}${repoNameSuffix}-app...`, and `repoNameSuffix` is a step
// parameter on the playbook — free-form JSON an operator types into the step
// form. create-application-handler's `dispatch` reads it as
// `parameters?.repoNameSuffix ?? ''` and passes it straight to
// `deriveRepoNames`.
//
// The panel used to omit it entirely, which is the defect this ticket owns. The
// obvious repair — insert the drawer's stage — would have been a SECOND wrong
// derivation: read live from the dev workflows table on 2026-09-18, the only
// provisioning playbook ("Create React SPA Application",
// a552c893-9e28-4c84-b853-9e80be3da796) sets `{"repoNameSuffix":"-dev"}` on all
// six resource-creating steps, and nothing ties that string to the stage the
// operator picks. A playbook authored with `-staging` would build `-staging`
// names for stage `dev`. So the suffix is READ from the selected playbook.
//
// ⚠️ THE STEPS MUST AGREE. Six steps each carry their own copy, and the handler
// reads whichever step is running. If they ever disagree the run would create a
// repository under one name and its pipeline under another, so a disagreement
// is reported rather than silently resolved — the caller blocks instead of
// checking names that only some of the run would use.

// ⚠️ STRUCTURAL, NOT THE PLAYBOOK TYPE. This reads two fields off each step and
// nothing else, so it asks for exactly those. Depending on the full
// `ExportSourcePlaybook` would couple the name check to every unrelated field
// that type carries — and `getPlaybookForExport`'s own result type already
// differs from it in fields this function never touches.
export interface SuffixSourceStep {
    readonly taskStateName?: string | null;
    readonly parameters?: unknown;
}

export interface SuffixSourcePhase {
    readonly steps?: readonly (SuffixSourceStep | null)[] | null;
}

export interface SuffixSourcePlaybook {
    readonly phases?: readonly (SuffixSourcePhase | null)[] | null;
}

// The steps whose `repoNameSuffix` decides a resource name. Taken from the live
// playbook and from the handler's switch in create-application-handler/index.ts.
const NAMING_TASK_STATES = [
    'CreateInfraRepo',
    'CreateSourceRepo',
    'SeedInfraRepo',
    'SeedSourceRepo',
    'ProvisionAppPipeline',
    'ProvisionInfraPipeline',
];

export interface RepoNameSuffixReading {
    // The suffix to derive with. Empty string is a real answer: a playbook that
    // sets none creates unsuffixed names, which is what prod does.
    readonly suffix: string;
    // True when every naming step that declared a suffix declared the SAME one.
    // False means the playbook would name resources inconsistently, and the
    // caller must not present a derived name as authoritative.
    readonly consistent: boolean;
    // The distinct values found, for the message shown when they disagree.
    readonly found: string[];
}

// ⚠️ AWSJSON IS DOUBLE-WRAPPED ON READ — see exportParameters in
// playbooks/utils/playbookDefinition.ts, verified live 2026-08-16 (FNP-1000,
// FNP-1010). DynamoDB holds `{"repoNameSuffix":"-dev"}` and this client
// receives that string JSON-encoded a second time. Peel exactly one layer when
// the value decodes to a string, then parse.
const parseStepParameters = (value: unknown): Record<string, unknown> | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    let raw: unknown = value;

    if (typeof raw === 'string') {
        try {
            const decoded = JSON.parse(raw) as unknown;
            // One layer peeled and still a string: that was the extra wrap.
            raw = typeof decoded === 'string' ? JSON.parse(decoded) : decoded;
        } catch {
            return undefined;
        }
    }

    return typeof raw === 'object' && raw !== null
        ? raw as Record<string, unknown>
        : undefined;
};

// The suffix the orchestrator will apply, read from a playbook's steps.
//
// A playbook with no naming steps, or none declaring a suffix, reads as the
// empty string and `consistent: true` — that is the handler's `?? ''`, not a
// failure to find anything.
export const readRepoNameSuffix = (
    playbook: SuffixSourcePlaybook | null | undefined,
): RepoNameSuffixReading => {
    const found = new Set<string>();

    for (const phase of playbook?.phases ?? []) {
        for (const step of phase?.steps ?? []) {
            if (!step) {
                continue;
            }
            const taskStateName = step.taskStateName ?? '';
            if (!NAMING_TASK_STATES.includes(taskStateName)) {
                continue;
            }

            const parameters = parseStepParameters(step.parameters);
            const suffix = parameters?.repoNameSuffix;

            // Only a string counts. A number or object here is malformed, and
            // treating it as absent would derive unsuffixed names against a
            // run that does something else.
            if (typeof suffix === 'string') {
                found.add(suffix);
            }
        }
    }

    const values = [...found];

    if (values.length === 0) {
        return {suffix: '', consistent: true, found: []};
    }

    return {
        suffix: values[0],
        consistent: values.length === 1,
        found: values,
    };
};
